import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider, appleProvider } from '../firebase/config';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

function normalizeUserType(rawUserType, rawRole, rawType, rawAccountType) {
  const candidates = [rawUserType, rawRole, rawType, rawAccountType]
    .map((v) => String(v || '').toLowerCase().trim())
    .filter(Boolean);

  if (candidates.some((v) => v === 'tenant' || v === 'huurder')) return 'tenant';
  if (candidates.some((v) => v === 'landlord' || v === 'verhuurder')) return 'landlord';
  return null;
}

function normalizeLandlordType(rawLandlordType, rawAccountType) {
  const candidates = [rawLandlordType, rawAccountType]
    .map((v) => String(v || '').toLowerCase().trim())
    .filter(Boolean);

  if (candidates.some((v) => v === 'agency' || v === 'kantoor')) return 'agency';
  if (candidates.some((v) => v === 'private' || v === 'particulier')) return 'private';
  return null;
}

function readCachedUserType() {
  try { return localStorage.getItem('userType') || null; } catch { return null; }
}
function readCachedLandlordType() {
  try { return localStorage.getItem('landlordType') || null; } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [landlordType, setLandlordType] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track whether login/OAuth already resolved the profile so onAuthStateChanged
  // can skip the redundant Firestore fetch that causes the flash.
  const profileResolvedRef = useRef(false);

  const applyUserProfile = useCallback(async (firebaseUser) => {
    let retries = 2;
    while (retries >= 0) {
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fetchedUserType = normalizeUserType(
            userData.userType, userData.role, userData.type, userData.accountType
          );

          if (fetchedUserType) {
            setUserType(fetchedUserType);
            localStorage.setItem('userType', fetchedUserType);

            if (fetchedUserType === 'landlord') {
              const fetchedLandlordType = normalizeLandlordType(userData.landlordType, userData.accountType);

              if (!fetchedLandlordType) {
                const createdAt = userData.createdAt;
                const updatedAt = userData.updatedAt;
                const now = new Date();
                const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
                const isVeryRecent =
                  (createdAt?.toDate && createdAt.toDate() > fiveMinutesAgo) ||
                  (updatedAt?.toDate && updatedAt.toDate() > fiveMinutesAgo);

                if (isVeryRecent) {
                  setLandlordType(null);
                } else {
                  try {
                    await setDoc(doc(db, 'users', firebaseUser.uid), {
                      landlordType: 'private', updatedAt: new Date()
                    }, { merge: true });
                  } catch { /* best-effort migration */ }
                  setLandlordType('private');
                  localStorage.setItem('landlordType', 'private');
                }
              } else {
                setLandlordType(fetchedLandlordType);
                localStorage.setItem('landlordType', fetchedLandlordType);
              }
            } else {
              setLandlordType(null);
              localStorage.removeItem('landlordType');
            }
          } else {
            setUserType(null);
            localStorage.removeItem('userType');
            localStorage.removeItem('landlordType');
          }
          return;
        }

        // No document — check localStorage for values pre-cached by the
        // sign-up flow (e.g. UserTypeSelectionLogin) before falling back to null.
        const cachedNewType = readCachedUserType();
        const cachedNewLandlord = readCachedLandlordType();
        const skeletonDoc = {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || '',
          name: firebaseUser.displayName || '',
          userType: cachedNewType || null,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        if (cachedNewType === 'landlord' && cachedNewLandlord) {
          skeletonDoc.landlordType = cachedNewLandlord;
        }
        await setDoc(doc(db, 'users', firebaseUser.uid), skeletonDoc);

        if (cachedNewType) {
          setUserType(cachedNewType);
          if (cachedNewType === 'landlord') {
            setLandlordType(cachedNewLandlord || null);
          }
        } else {
          setUserType(null);
          localStorage.removeItem('userType');
        }
        return;
      } catch (error) {
        console.error('AuthContext: Error fetching user profile (retries left:', retries, '):', error);
        retries--;
        if (retries < 0) {
          // All retries exhausted — use cached value so user isn't stuck.
          const cached = readCachedUserType();
          if (cached) {
            setUserType(cached);
            if (cached === 'landlord') {
              setLandlordType(readCachedLandlordType() || 'private');
            }
          } else {
            setUserType(null);
          }
          return;
        }
        await new Promise((r) => setTimeout(r, 800));
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // If login() or signInWithOAuth already resolved the profile, skip the
        // duplicate Firestore round-trip that was causing the loading flash.
        if (profileResolvedRef.current) {
          profileResolvedRef.current = false;
          setLoading(false);
          return;
        }

        // Immediately apply cached values so the UI doesn't flash a spinner
        // while we confirm the profile from Firestore.
        const cachedType = readCachedUserType();
        if (cachedType) {
          setUserType(cachedType);
          if (cachedType === 'landlord') {
            setLandlordType(readCachedLandlordType() || null);
          }
        }

        // Keep loading true until Firestore confirms.
        setLoading(true);
        await applyUserProfile(firebaseUser);
      } else {
        setUser(null);
        setUserType(null);
        setLandlordType(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [applyUserProfile]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const result = await signInWithEmailAndPassword(auth, email, password);
      await reload(result.user);
      
      if (!result.user.emailVerified) {
        toast.error('Please verify your email address before logging in.');
        throw new Error('EMAIL_NOT_VERIFIED');
      }
      
      // Resolve profile before onAuthStateChanged fires so it doesn't flash.
      await applyUserProfile(result.user);
      profileResolvedRef.current = true;
      
      toast.success('Successfully logged in!');
      sessionStorage.setItem('lastLoginAt', String(Date.now()));
      return result;
    } catch (error) {
      if (error.message === 'EMAIL_NOT_VERIFIED') throw error;
      
      let errorMessage = 'Login failed. Please try again.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password.';
      }
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, displayName, userType) => {
    try {
      setLoading(true);
      
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile
      await updateProfile(user, { displayName });
      
      // Send email verification
      try {
        await sendEmailVerification(user, {
          url: 'https://studystay.be/auth/action',
          handleCodeInApp: false
        });
        
      } catch (verificationError) {
        console.error('Error sending verification email:', verificationError);
        // Don't fail registration if email sending fails, but log it
        toast.error('Account created, but verification email could not be sent. Please try resending from the verification page.');
      }
      
      // Save user data to Firestore - compatible with iOS app structure
      await setDoc(doc(db, 'users', user.uid), {
        email,
        displayName,
        name: displayName,
        contactName: displayName,
        userType: userType || 'tenant',
        createdAt: new Date(),
        updatedAt: new Date(),
        // iOS app compatibility fields
        phone: '',
        address: '',
        bio: '',
        profileImageUrl: '',
        isActive: true
      });
      
      
      toast.success('Account created! Please verify your email address.');
      // Reload user to get updated emailVerified status
      await reload(user);
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      }
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithOAuthProvider = async (provider, providerName) => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      const oauthUser = result.user;

      // OAuth providers (Google/Apple) verify the email on their side,
      // so we treat emailVerified as true if the provider confirms it.
      // Firebase may not automatically set emailVerified for all OAuth flows.
      if (!oauthUser.emailVerified && oauthUser.providerData?.length > 0) {
        await reload(oauthUser);
      }

      await applyUserProfile(oauthUser);
      profileResolvedRef.current = true;

      // Backfill displayName if missing in Firestore.
      try {
        const snap = await getDoc(doc(db, 'users', oauthUser.uid));
        if (snap.exists() && !snap.data().displayName && oauthUser.displayName) {
          await setDoc(doc(db, 'users', oauthUser.uid), {
            displayName: oauthUser.displayName,
            name: oauthUser.displayName,
            updatedAt: new Date()
          }, { merge: true });
        }
      } catch { /* best-effort */ }

      sessionStorage.setItem('lastLoginAt', String(Date.now()));
      return result;
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return null;
      }
      if (error.code === 'auth/account-exists-with-different-credential') {
        toast.error('Er bestaat al een account met dit e-mailadres via een andere inlogmethode.');
      } else {
        toast.error(`${providerName} inloggen mislukt. Probeer het opnieuw.`);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => signInWithOAuthProvider(googleProvider, 'Google');
  const signInWithApple = () => signInWithOAuthProvider(appleProvider, 'Apple');

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin + '/login',
      handleCodeInApp: false
    });
  };

  const logout = async () => {
    try {
      await signOut(auth);
      try { localStorage.removeItem('userType'); } catch {}
      try { localStorage.removeItem('landlordType'); } catch {}
      try { sessionStorage.removeItem('lastLoginAt'); } catch {}
      toast.success('Successfully logged out!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const refreshUserType = async () => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fetchedUserType = normalizeUserType(
            userData.userType,
            userData.role,
            userData.type,
            userData.accountType
          ) || 'tenant';
          setUserType(fetchedUserType);
          
          if (fetchedUserType === 'landlord') {
            const fetchedLandlordType = normalizeLandlordType(
              userData.landlordType,
              userData.accountType
            );
            if (fetchedLandlordType) {
              setLandlordType(fetchedLandlordType);
            } else {
              setLandlordType('private');
            }
          } else {
            setLandlordType(null);
          }
        } else {
          setUserType(null);
          setLandlordType(null);
        }
      } catch (error) {
        console.error('Error refreshing user type:', error);
        setUserType(null);
        setLandlordType(null);
      }
    }
  };

  const setUserTypeManually = async (newUserType) => {
    if (!user) {
      return;
    }
    
    try {
      try {
        await setDoc(doc(db, 'users', user.uid), {
          userType: newUserType,
          updatedAt: new Date()
        }, { merge: true });
      } catch (firestoreError) {
        console.warn('Firestore update failed, using local state only:', firestoreError);
      }
      
      setUserType(newUserType);
      localStorage.setItem('userType', newUserType);
      toast.success(`Account type set to ${newUserType}`);
    } catch (error) {
      console.error('AuthContext: Error setting userType:', error);
      toast.error('Failed to update account type');
    }
  };

  const value = {
    user,
    userType,
    landlordType,
    loading,
    login,
    register,
    logout,
    signInWithGoogle,
    signInWithApple,
    resetPassword,
    refreshUserType,
    setUserTypeManually
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
